import { Flex, Form, Skeleton } from 'antd'
import { T, VStack } from 'govwell-ui'
import { twMerge } from 'tailwind-merge'

import { Logo, LogoColor } from 'src/components/svgs/Logo'
import { EmailTemplateTypeButtonTextMap } from 'src/constants'
import { useRecordEmailPreviewDataQuery } from 'src/fetch/emailPreview'
import { DateFormats, formatDateInTimeZone } from 'src/utils/date'

import { EmailTemplateType } from '../../../types/graphql'
import { useMyOrg } from '../../layouts/AppStateContextLayout/utils'

import { EmailTemplateFormValues } from './types'

type Props = {
  buttonText?: string
  isExpirationNotification?: boolean
  recordId?: number
}
export const EmailPreview = ({ buttonText, isExpirationNotification, recordId }: Props) => {
  const form = Form.useFormInstance()
  Form.useWatch([], form)
  const { content, signature, type, subject } = form.getFieldsValue() as EmailTemplateFormValues

  return (
    <VStack align="stretch" gap={3} padding="12px 0 0 24px" className="border-l border-neutral-200">
      <T.H3>Preview</T.H3>
      <T.BodyLarge>Subject: {subject}</T.BodyLarge>
      <EmailPreviewContent
        buttonText={buttonText}
        content={content}
        isExpirationNotification={!!isExpirationNotification}
        recordId={recordId}
        signature={signature}
        isSignatureHidden={false}
        type={type}
      />
    </VStack>
  )
}

type EmailPreviewContentProps = {
  buttonText?: string
  content: string
  isExpirationNotification?: boolean
  recipients?: readonly string[]
  recordId?: number
  subject?: string
  type?: EmailTemplateType
} & (
  | { isSignatureHidden: true; signature?: never }
  | { isSignatureHidden: false; signature: string }
)
// Previews the email generated by api/src/emails/EmailTemplateEmail.tsx
export const EmailPreviewContent = ({
  buttonText,
  content,
  isExpirationNotification,
  recipients,
  recordId,
  signature,
  subject,
  type,
  isSignatureHidden = false,
}: EmailPreviewContentProps) => {
  const logoUrl = useMyOrg()?.logoUrl
  const callToAction = buttonText || (type ? EmailTemplateTypeButtonTextMap[type] : undefined)
  const timeZone = useMyOrg()?.timeZone

  const { data: recordData, isLoading: isLoadingRecordData } =
    useRecordEmailPreviewDataQuery(recordId)
  const expirationDate = recordData?.recordById?.expirationDate
  const recordName = recordData?.recordById?.formattedName

  const getTextClassName = (val?: string) => (val ? 'text-neutral-950' : 'text-neutral-500')

  if (isLoadingRecordData) {
    return <Skeleton active />
  }

  const hasEmailHeaders = !!recipients || !!subject

  const emailCard = (
    <section className="flex flex-col items-start justify-center gap-6 rounded-sm bg-neutral-100 p-8">
      <div className="flex w-full flex-col gap-6 rounded-lg border border-neutral-200 bg-white p-8">
        {logoUrl && (
          <img
            alt="Organization Logo"
            src={logoUrl}
            width="200"
            className="max-h-[100px] max-w-[200px] object-contain"
          />
        )}
        <T.Body className={twMerge('whitespace-pre-wrap', getTextClassName(content))}>
          {content ? content.trim() : 'Email content goes here'}
        </T.Body>
        {!!recordName?.trim() && <PreviewItem label="Record" value={recordName} />}
        {isExpirationNotification && !!expirationDate && (
          <PreviewItem
            label="Expiration Date"
            value={formatDateInTimeZone(expirationDate, timeZone, DateFormats.MonthNameDateNoTime)}
          />
        )}
        {callToAction && (
          <div className="text-md my-2 w-full cursor-pointer rounded-lg bg-neutral-950 p-3 text-center text-white">
            {callToAction}
          </div>
        )}
        {!isSignatureHidden && (
          <T.Body className={twMerge('whitespace-pre-wrap', getTextClassName(signature))}>
            {signature ? signature.trim() : 'Signature goes here'}
          </T.Body>
        )}
      </div>
      <VStack align="center" gap={2}>
        <Logo color={LogoColor.Black} showText={true} height={36} />
        <T.BodySmall className="text-center text-neutral-600">
          GovWell Technologies Inc.
          <br />
          228 Park Ave S PMB 368134, New York, NY 10003-1502
        </T.BodySmall>
      </VStack>
    </section>
  )

  if (!hasEmailHeaders) {
    return emailCard
  }

  return (
    <div className="overflow-hidden rounded-lg border border-solid border-neutral-200">
      {recipients && (
        <VStack
          gap={0}
          align="stretch"
          className="border-b border-solid border-neutral-200 bg-neutral-50 px-3.5 py-1.5"
        >
          <T.BodySmall className="text-neutral-400">To</T.BodySmall>
          <T.BodySmall className="text-neutral-700">
            {recipients.length > 0 ? (
              recipients.join(', ')
            ) : (
              <span className="text-neutral-400 italic">No recipients added yet</span>
            )}
          </T.BodySmall>
        </VStack>
      )}
      {subject && (
        <VStack
          gap={0}
          align="stretch"
          className="border-b border-solid border-neutral-200 bg-neutral-50 px-3.5 py-1.5"
        >
          <T.BodySmall className="text-neutral-400">Subject</T.BodySmall>
          <T.BodySmall className="font-semibold text-neutral-700">{subject}</T.BodySmall>
        </VStack>
      )}
      {emailCard}
    </div>
  )
}

type PreviewItemProps = {
  label: string
  value: string
}
const PreviewItem = ({ label, value }: PreviewItemProps) => {
  return (
    <Flex vertical>
      <T.Body className="text-neutral-700">{label}</T.Body>
      <T.Body>{value}</T.Body>
    </Flex>
  )
}
